import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NbCardModule, NbButtonModule, NbInputModule, NbSpinnerModule, NbDialogRef, NbIconModule } from '@nebular/theme';
import { CampaignService } from 'src/app/deebr/service/campaign.service';
import { environment } from 'src/environments/environment';

interface CampaignRow {
    id: number;
    name: string;
    date: string;
    volume: number;
    volumeOriginal: number;
    volumePct: number;
    lift: string;
    potential: string;
    pressure: number;
    removed: number;
    curve: { x: number; y: number }[] | null; // preloaded lift curve
    scopeCount: number; // filtered base size (0 = no filters, use totalMails)
    autoIncluded: boolean; // true if campaign was auto-added due to capped_with link
    isFilterOnly: boolean; // true if segment uses only CRM filters (no ML categories)
}

interface OverlapData {
    campaigns: { id: number; name: string; total: number }[];
    intersections: { sets: number[]; count: number }[];
}

@Component({
    selector: 'app-optimize-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, NbCardModule, NbButtonModule, NbInputModule, NbSpinnerModule, NbIconModule],
    template: `
        <nb-card style="width: 950px; max-width: 95vw; max-height: 90vh; margin: 0;">
            <nb-card-header>
                <h5 class="mb-0">Optimiser les campagnes</h5>
            </nb-card-header>
            <nb-card-body style="overflow-y: auto;">
                <!-- Fatigue Management Rules -->
                <div class="mb-4">
                    <h6 class="text-muted mb-3">Regles de gestion de la fatigue</h6>
                    <div class="d-flex align-items-center mb-2">
                        <span class="me-2">Maximum</span>
                        <input type="number" nbInput fieldSize="small" style="width: 60px; text-align: center;"
                            [(ngModel)]="maxMessagesPerUser" (ngModelChange)="markChanged()" min="1" max="20">
                        <span class="ms-2">messages par client</span>
                    </div>
                    <div class="d-flex align-items-center">
                        <span class="me-2">Maximum</span>
                        <input type="number" nbInput fieldSize="small" style="width: 60px; text-align: center;"
                            [(ngModel)]="maxMessagesPerDay" (ngModelChange)="markChanged()" min="1" max="10">
                        <span class="ms-2">message par client et par jour</span>
                    </div>
                    <div *ngIf="hasFilterAndIA" class="d-flex align-items-center mt-3"
                        style="padding: 8px 12px; background: #f7f9fc; border-radius: 6px;">
                        <span class="me-2">En cas d'overlap, priorite aux segments</span>
                        <select class="form-select form-select-sm" style="width: auto;"
                            [(ngModel)]="filterPriority" (ngModelChange)="markChanged()">
                            <option value="ia">IA (predictifs)</option>
                            <option value="filter">Filtre (manuels)</option>
                        </select>
                    </div>
                </div>

                <!-- Auto-included info banner -->
                <div *ngIf="unlinkedCampaigns.length > 0 || autoIncludedIds.size > 0" class="mb-3">
                    <div *ngIf="autoIncludedIds.size > 0" class="alert alert-info small py-2 mb-2">
                        <nb-icon icon="link-2-outline" class="me-1"></nb-icon>
                        Des campagnes liees ont ete ajoutees automatiquement pour garantir la coherence de l'optimisation.
                    </div>
                    <div *ngIf="unlinkedCampaigns.length > 0" class="alert alert-warning small py-2 mb-0">
                        <nb-icon icon="unlock-outline" class="me-1"></nb-icon>
                        {{ unlinkedCampaigns.length }} campagne(s) deliee(s) — leur capping sera reinitialise a l'application.
                    </div>
                </div>

                <!-- Campaigns Table -->
                <h6 class="text-muted mb-3">Campagnes</h6>
                <table class="table table-sm table-bordered align-middle">
                    <thead class="table-light">
                        <tr>
                            <th></th>
                            <th>Nom</th>
                            <th>Date</th>
                            <th class="text-end">Volume</th>
                            <th style="width: 200px;">Pression</th>
                            <th class="text-center">Lift</th>
                            <th class="text-center">Potentiel</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let row of campaignRows">
                            <td class="text-center">
                                <nb-icon [icon]="row.date === todayStr ? 'email-outline' : 'calendar-outline'"
                                    pack="eva" style="font-size: 1.2rem;"></nb-icon>
                            </td>
                            <td>
                                {{ row.name }}
                                <span *ngIf="row.autoIncluded" class="badge bg-info text-white ms-1" style="font-size: 0.65rem;">liee</span>
                                <span *ngIf="row.isFilterOnly" class="badge bg-secondary text-white ms-1" style="font-size: 0.65rem;">filtre</span>
                                <div *ngIf="row.scopeCount > 0" class="text-muted small" style="font-size: 0.75rem;">
                                    Base filtree : {{ row.scopeCount | number }}
                                </div>
                            </td>
                            <td>{{ row.date }}</td>
                            <td class="text-end" style="min-width: 110px;">
                                <ng-container *ngIf="totalMails > 0 || optimized; else volumeLoading">
                                    <span class="fw-bold">{{ row.volume | number }}</span>
                                    <div *ngIf="optimized && row.removed > 0" class="text-danger small">
                                        -{{ row.removed | number }} retires
                                    </div>
                                </ng-container>
                                <ng-template #volumeLoading>
                                    <span class="text-muted">—</span>
                                </ng-template>
                            </td>
                            <td>
                                <ng-container *ngIf="!row.isFilterOnly">
                                    <div class="d-flex align-items-center gap-2">
                                        <input type="range" class="pressure-range" min="1" max="100"
                                            [(ngModel)]="row.pressure"
                                            (input)="onPressureInput(row)"
                                            style="flex: 1;">
                                        <span class="small text-muted" style="min-width: 30px;">{{ row.pressure }}%</span>
                                    </div>
                                </ng-container>
                                <span *ngIf="row.isFilterOnly" class="small text-muted">100% (filtre)</span>
                            </td>
                            <td class="text-center" style="min-width: 60px;">
                                <ng-container *ngIf="row.isFilterOnly"><span class="text-muted">—</span></ng-container>
                                <ng-container *ngIf="!row.isFilterOnly">
                                    <ng-container *ngIf="row.curve; else liftLoading">{{ row.lift }}x</ng-container>
                                    <ng-template #liftLoading><span class="text-muted">—</span></ng-template>
                                </ng-container>
                            </td>
                            <td class="text-center" style="min-width: 70px;">
                                <ng-container *ngIf="row.isFilterOnly"><span class="text-muted">—</span></ng-container>
                                <ng-container *ngIf="!row.isFilterOnly">
                                    <ng-container *ngIf="row.curve; else potLoading">{{ row.potential }}%</ng-container>
                                    <ng-template #potLoading><span class="text-muted">—</span></ng-template>
                                </ng-container>
                            </td>
                            <td class="text-center" style="width: 40px;">
                                <button *ngIf="campaignRows.length > 2" nbButton ghost status="danger" size="tiny"
                                    (click)="unlinkCampaign(row)" title="Delier cette campagne">
                                    <nb-icon icon="link-2-outline"></nb-icon>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Overlap Section — always visible once loaded, greyed out when stale -->
                <div *ngIf="overlapData" class="mt-4 position-relative"
                    [style.opacity]="overlapIsStale ? '0.45' : '1'"
                    [style.pointer-events]="overlapIsStale ? 'none' : 'auto'"
                    [style.transition]="'opacity 0.2s'">
                    <h6 class="text-muted mb-3">Overlap entre campagnes</h6>

                    <div class="d-flex gap-3 align-items-start">
                        <!-- Venn Diagram (2-3 campaigns) -->
                        <div *ngIf="showVenn" #vennContainer style="height: 220px; flex: 0 0 55%;"></div>

                        <!-- Matrix Table -->
                        <div [style.flex]="showVenn ? '1' : '1 1 100%'">
                            <table class="table table-sm table-bordered text-center align-middle mb-0">
                                <thead class="table-light">
                                    <tr>
                                        <th></th>
                                        <th *ngFor="let c of overlapData.campaigns" class="small">
                                            {{ c.name }}
                                            <div class="text-muted" style="font-weight: normal;">{{ c.total | number }}</div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngFor="let row of overlapData.campaigns">
                                        <td class="table-light fw-bold small text-start">{{ row.name }}</td>
                                        <td *ngFor="let col of overlapData.campaigns"
                                            [class.table-primary]="row.id === col.id">
                                            <span *ngIf="row.id === col.id">{{ row.total | number }}</span>
                                            <span *ngIf="row.id !== col.id">
                                                {{ getIntersection(row.id, col.id) | number }}
                                                <div class="text-muted small" *ngIf="getIntersectionPct(row.id, col.id, row.total) as pct">
                                                    ({{ pct }}%)
                                                </div>
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <!-- Spinner while calculating for the first time -->
                <div *ngIf="isOptimizing && !overlapData" class="mt-4 text-center text-muted small py-3">
                    <span [nbSpinner]="true" nbSpinnerStatus="primary" nbSpinnerSize="small"></span>
                    Chargement en cours...
                </div>
            </nb-card-body>
            <nb-card-footer class="d-flex justify-content-end">
                <div class="d-flex gap-2">
                    <button nbButton status="basic" (click)="close()">Annuler</button>
                    <button nbButton status="primary"
                        [nbSpinner]="isOptimizing" nbSpinnerStatus="control"
                        [disabled]="isOptimizing || isLoadingOverlap || !hasChanges || !canCalculate"
                        (click)="calculate()">
                        Calculer
                    </button>
                    <button *ngIf="optimized" nbButton status="success"
                        [nbSpinner]="isApplying" nbSpinnerStatus="control"
                        [disabled]="isApplying || hasChanges"
                        (click)="apply()">
                        Appliquer
                    </button>
                </div>
            </nb-card-footer>
        </nb-card>
    `,
    styles: [`
        :host { display: block; }
        table th, table td { vertical-align: middle; }
        .pressure-range {
            -webkit-appearance: none;
            appearance: none;
            height: 4px;
            border-radius: 2px;
            background: #d0d0d0;
            cursor: pointer;
            outline: none;
        }
        .pressure-range::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background: #6c757d;
            cursor: pointer;
            border: 2px solid #fff;
            box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }
        .pressure-range::-moz-range-thumb {
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background: #6c757d;
            cursor: pointer;
            border: 2px solid #fff;
            box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }
    `]
})
export class OptimizeDialogComponent implements OnInit, OnDestroy {
    // Injected via NbDialogRef context
    campaigns: any[] = [];
    autoIncludedIds: Set<number> = new Set();

    maxMessagesPerUser = 3;
    maxMessagesPerDay = 1;
    filterPriority: 'ia' | 'filter' = 'ia'; // ia = segments IA prioritaires, filter = segments filtre prioritaires
    totalMails = 0;
    campaignRows: CampaignRow[] = [];
    hasChanges = true; // true at open so first Calculer is always clickable
    isOptimizing = false;
    isApplying = false;
    isLoadingOverlap = false;
    optimized = false;
    todayStr = new Date().toISOString().slice(0, 10);

    // Unlinked campaigns (removed from group, will have capped_with cleared on apply)
    unlinkedCampaigns: { id: number; campaign: any }[] = [];

    // Overlap
    overlapData: OverlapData | null = null;
    overlapIsStale = false;
    showVenn = false;
    private vennChart: any = null;

    @ViewChild('vennContainer') vennContainer?: ElementRef;

    constructor(
        protected ref: NbDialogRef<OptimizeDialogComponent>,
        private campaignService: CampaignService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit() {
        // Load total CRM email count for slider volume estimates
        this.campaignService.getTotalMails().subscribe(n => {
            this.totalMails = n;
            // Only estimate volume for rows that have no saved volume yet
            this.campaignRows.forEach(row => {
                if (row.volume === 0) {
                    const base = row.scopeCount > 0 ? row.scopeCount : n;
                    row.volume = Math.round(base * row.pressure / 100);
                }
            });
        });

        // Restore saved capping rules from any campaign that has them
        for (const event of this.campaigns) {
            const meta = event.meta || event;
            const segs = meta.segments || {};
            if (segs?.capping) {
                this.maxMessagesPerUser = segs.capping.max_per_user || 3;
                this.maxMessagesPerDay = segs.capping.max_per_day || 1;
                break;
            }
        }

        this.campaignRows = this.campaigns.map(event => {
            const meta = event.meta || event;
            const stats = meta.stats || {};
            const segs = meta.segments || {};
            // Detect filter-only: no audiences or all audience groups empty
            const audiences = segs?.audiences || [];
            const flatAudiences = audiences.flat ? audiences.flat() : audiences;
            const isFilterOnly = flatAudiences.length === 0;
            return {
                id: meta.id,
                name: meta.name || event.title || '',
                date: meta.date || (event.start ? new Date(event.start).toISOString().slice(0, 10) : ''),
                volume: stats.userCount || 0,
                volumeOriginal: stats.userCount || 0,
                volumePct: 0,
                lift: stats.lift || '0',
                potential: stats.potential || '0',
                pressure: isFilterOnly ? 100 : (segs?.pressure || 100),
                removed: 0,
                curve: null,
                scopeCount: 0,
                autoIncluded: this.autoIncludedIds.has(meta.id),
                isFilterOnly,
            };
        });

        // Pre-load curves via the overlap endpoint (simpler than optimize — no fatigue redistribution)
        // so that the slider lift/potential update in real-time while calculate() is still loading
        const preloadIds = this.campaignRows.map(r => r.id);
        const preloadPressures: Record<number, number> = {};
        this.campaignRows.forEach(r => preloadPressures[r.id] = r.pressure);
        this.campaignService.getOverlap(preloadIds, preloadPressures, this.maxMessagesPerUser, this.maxMessagesPerDay, true).subscribe({
            next: (data) => {
                // Fill scopeCount from overlap response so "Base filtrée" shows immediately
                if (data.campaigns) {
                    for (const camp of data.campaigns) {
                        const row = this.campaignRows.find(r => r.id === camp.id);
                        if (row && camp.scope_count) row.scopeCount = camp.scope_count;
                    }
                }
                if (data.curves) {
                    this.campaignRows.forEach(row => {
                        const c = data.curves![String(row.id)];
                        if (c && !row.curve) row.curve = c; // only if calculate() hasn't already set it
                    });
                }
            }
        });

        // Auto-calculate at open: gets precise volumes, removed counts, overlap and curves
        this.calculate();
    }

    ngOnDestroy() {
        this.disposeVenn();
    }

    /** Slider move: update volume + lift/potential from preloaded curve (no backend call) */
    onPressureInput(row: CampaignRow) {
        // Volume: use filtered base if campaign has filters, otherwise total base
        const base = row.scopeCount > 0 ? row.scopeCount : this.totalMails;
        row.volume = Math.round(base * row.pressure / 100);

        // Lift/Potential: read from preloaded curve — clamp to max curve range
        if (row.curve && row.curve.length > 0) {
            const maxX = row.curve[row.curve.length - 1].x;
            const clampedPct = Math.min(row.pressure, maxX);
            const point = row.curve.find(p => p.x === clampedPct)
                ?? row.curve.reduce((prev, curr) =>
                    Math.abs(curr.x - clampedPct) < Math.abs(prev.x - clampedPct) ? curr : prev
                );
            if (point) {
                row.lift = point.y.toFixed(1);
                row.potential = Math.round(clampedPct * point.y).toString();
            }
        }

        this.markChanged();
    }

    get hasFilterAndIA(): boolean {
        return this.campaignRows.some(r => r.isFilterOnly) && this.campaignRows.some(r => !r.isFilterOnly);
    }

    unlinkCampaign(row: CampaignRow) {
        const campaign = this.campaigns.find(c => (c.meta?.id || c.id) === row.id);
        this.unlinkedCampaigns.push({ id: row.id, campaign });
        this.campaignRows = this.campaignRows.filter(r => r.id !== row.id);
        this.optimized = false;
        this.markChanged();
    }

    get canCalculate(): boolean {
        return this.campaignRows.length >= 2;
    }

    markChanged() {
        this.hasChanges = true;
        if (this.overlapData) {
            this.overlapIsStale = true;
        }
    }

    /** Full optimize: applies capping/fatigue rules, shows removed users */
    calculate() {
        this.isOptimizing = true;

        const ids = this.campaignRows.map(r => r.id);
        const pressures: Record<number, number> = {};
        this.campaignRows.forEach(r => pressures[r.id] = r.pressure);

        this.campaignService.optimizeCampaigns(ids, this.maxMessagesPerUser, this.maxMessagesPerDay, pressures, true, this.filterPriority).subscribe({
            next: (res) => {
                for (const opt of res.campaigns) {
                    const row = this.campaignRows.find(r => r.id === opt.id);
                    if (row) {
                        row.volume = opt.volume;
                        row.volumeOriginal = opt.volume_original;
                        row.removed = opt.removed;
                        row.lift = opt.lift.toFixed(1);
                        row.potential = opt.potential.toFixed(1);
                        row.volumePct = opt.volume_pct;
                        if (opt.scope_count !== undefined) row.scopeCount = opt.scope_count;
                    }
                }
                // Populate lift curves from optimize response (avoids separate previewPlanning calls)
                if (res.curves) {
                    this.campaignRows.forEach(row => {
                        const curve = res.curves![String(row.id)];
                        if (curve) row.curve = curve;
                    });
                }
                this.optimized = true;
                this.hasChanges = false;
                this.isOptimizing = false;
                // Update overlap with post-fatigue data from the optimize response
                if (res.overlap) {
                    this.disposeVenn();
                    this.overlapData = null;
                    this.showVenn = false;
                    this.overlapIsStale = false;
                    this.cdr.detectChanges();
                    this.overlapData = res.overlap;
                    this.showVenn = res.overlap.campaigns.length <= 3;
                    this.cdr.detectChanges();
                    if (this.showVenn) {
                        setTimeout(() => this.drawVenn(), 50);
                    }
                }
            },
            error: (err) => {
                console.error('Optimize failed', err);
                this.isOptimizing = false;
            }
        });
    }

    /** Load/refresh overlap on demand — uses current slider pressures */
    loadOverlap() {
        this.isLoadingOverlap = true;

        // Reset overlap section to force full DOM re-creation (fixes Venn redraw)
        this.disposeVenn();
        this.overlapData = null;
        this.showVenn = false;
        this.overlapIsStale = false;
        this.cdr.detectChanges();

        const ids = this.campaignRows.map(r => r.id);
        const pressures: Record<number, number> = {};
        this.campaignRows.forEach(r => pressures[r.id] = r.pressure);

        this.campaignService.getOverlap(ids, pressures, this.maxMessagesPerUser, this.maxMessagesPerDay, true).subscribe({
            next: (data) => {
                // Populate per-campaign lift curves from this single response
                if (data.curves) {
                    this.campaignRows.forEach(row => {
                        const curve = data.curves![String(row.id)];
                        if (curve) row.curve = curve;
                    });
                }
                this.overlapData = data;
                this.showVenn = data.campaigns.length <= 3;
                this.hasChanges = false;
                this.isLoadingOverlap = false;
                this.cdr.detectChanges(); // ensure DOM is created before drawing
                if (this.showVenn) {
                    setTimeout(() => this.drawVenn(), 50);
                }
            },
            error: (err) => {
                console.error('Overlap failed', err);
                this.isLoadingOverlap = false;
            }
        });
    }

    private drawVenn() {
        if (!this.showVenn || !this.vennContainer || !this.overlapData) return;

        try {
            anychart.licenseKey(environment.anyChartLicenseKey);

            const vennData: any[] = [];

            for (const camp of this.overlapData.campaigns) {
                vennData.push({ x: [camp.name], value: camp.total, name: camp.name });
            }

            for (const inter of this.overlapData.intersections) {
                const names = inter.sets.map(id => {
                    const c = this.overlapData!.campaigns.find(c => c.id === id);
                    return c?.name || String(id);
                });
                vennData.push({ x: names, value: inter.count, name: `${inter.count} en commun` });
            }

            this.vennChart = anychart.venn(vennData);
            this.vennChart.container(this.vennContainer.nativeElement);
            this.vennChart.intersections().labels(true);
            this.vennChart.tooltip().format('{%name}: {%value}');
            this.vennChart.credits().enabled(false);
            this.vennChart.legend().enabled(true);
            this.vennChart.draw();
        } catch (e) {
            console.error('Venn chart error', e);
        }
    }

    private disposeVenn() {
        if (this.vennChart) {
            try { this.vennChart.dispose(); } catch (_) { }
            this.vennChart = null;
        }
    }

    getIntersection(id1: number, id2: number): number {
        if (!this.overlapData) return 0;
        const inter = this.overlapData.intersections.find(i =>
            i.sets.length === 2 && i.sets.includes(id1) && i.sets.includes(id2)
        );
        return inter?.count || 0;
    }

    getIntersectionPct(id1: number, id2: number, total: number): string {
        if (!total) return '';
        const count = this.getIntersection(id1, id2);
        if (!count) return '';
        return Math.round((count / total) * 100).toString();
    }

    apply() {
        this.isApplying = true;
        const cappedIds = this.campaignRows.map(r => r.id);

        // Updates for optimized campaigns
        const updates = this.campaignRows.map(row => {
            const campaign = this.campaigns.find(c => (c.meta?.id || c.id) === row.id);
            const meta = campaign?.meta || campaign;
            const existingSegments = meta.segments || {};

            const segments = {
                ...existingSegments,
                pressure: row.pressure,
                capping: {
                    max_per_user: this.maxMessagesPerUser,
                    max_per_day: this.maxMessagesPerDay,
                    capped_with: cappedIds.filter(id => id !== row.id),
                    stale: false,
                },
                stats: {
                    ...(existingSegments.stats || {}),
                    numb_mail: row.volume,
                    lift: parseFloat(row.lift),
                    potential: parseFloat(row.potential),
                },
            };

            return this.campaignService.updateScheduledCampaign(row.id, {
                name: meta.name || row.name,
                date: meta.date || row.date,
                segments,
                status: meta.status || 'DRAFT',
            });
        });

        // Clear capped_with for unlinked campaigns
        const unlinkUpdates = this.unlinkedCampaigns.map(({ id, campaign }) => {
            const meta = campaign?.meta || campaign;
            const existingSegments = meta?.segments || {};
            const segments = {
                ...existingSegments,
                capping: null,
            };
            return this.campaignService.updateScheduledCampaign(id, {
                name: meta?.name || '',
                date: meta?.date || '',
                segments,
                status: meta?.status || 'DRAFT',
            });
        });

        const allUpdates = [...updates, ...unlinkUpdates];
        let completed = 0;
        allUpdates.forEach(obs => {
            obs.subscribe({
                next: () => {
                    completed++;
                    if (completed === allUpdates.length) {
                        this.isApplying = false;
                        this.ref.close({ applied: true });
                    }
                },
                error: (err: any) => {
                    console.error('Update failed', err);
                    completed++;
                    if (completed === allUpdates.length) {
                        this.isApplying = false;
                        this.ref.close({ applied: true });
                    }
                }
            });
        });
    }

    close() {
        this.disposeVenn();
        this.ref.close();
    }
}
